import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/hooks/useAuth";
import { usePWAInstall } from "@/hooks/usePWAInstall";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Download, X } from "lucide-react";

interface PWAInstallBannerProps {
  className?: string;
}

export function PWAInstallBanner({ className }: PWAInstallBannerProps) {
  const { t } = useLanguage();
  const { roles } = useAuth();
  const { isBannerVisible, triggerInstall, dismissTemporarily } = usePWAInstall();

  // Determine icon based on role (employees get purple, owners/tenants get black)
  const isInternalUser = roles.some((r) => r.role === "employee");
  const iconSrc = isInternalUser
    ? "/icons/icon-purple-512.png"
    : "/icons/icon-black-512.png";

  // Don't render if banner shouldn't be visible
  if (!isBannerVisible) return null;

  return (
    <div
      className={cn(
        // Fixed positioning at bottom
        "fixed bottom-0 inset-x-0 z-50",
        // Background with gradient and blur
        "bg-gradient-to-r from-primary/95 to-primary/85",
        "backdrop-blur-lg",
        // Border and shadow
        "border-t border-primary/20",
        "shadow-[0_-4px_20px_rgba(0,0,0,0.15)]",
        // Padding with safe area for notched devices
        "p-4 pb-[max(1rem,env(safe-area-inset-bottom))]",
        // Rounded top corners
        "rounded-t-2xl",
        // Animation
        "animate-slide-up",
        className
      )}
    >
      <div className="flex items-center gap-4 max-w-lg mx-auto">
        {/* App Icon */}
        <div className="flex-shrink-0">
          <img
            src={iconSrc}
            alt={t("أيقونة دلّالي", "Dallali Icon")}
            className="w-14 h-14 rounded-xl shadow-lg"
          />
        </div>

        {/* Text Content */}
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-primary-foreground text-base leading-tight">
            {t("ثبّت تطبيق دلّالي", "Install Dallali App")}
          </h3>
          <p className="text-primary-foreground/80 text-sm mt-0.5 leading-snug">
            {t(
              "تجربة أسرع ووصول أسهل لخدماتك.",
              "Faster experience and easier access to your services."
            )}
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Install Button */}
          <Button
            onClick={triggerInstall}
            size="sm"
            className="bg-primary-foreground text-primary hover:bg-primary-foreground/90 font-medium gap-1.5"
          >
            <Download className="h-4 w-4" />
            {t("تثبيت", "Install")}
          </Button>

          {/* Dismiss Button */}
          <Button
            onClick={dismissTemporarily}
            size="icon"
            variant="ghost"
            className="text-primary-foreground/70 hover:text-primary-foreground hover:bg-primary-foreground/10 h-8 w-8"
            aria-label={t("لاحقاً", "Later")}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
